/**
 * Multipart routes (1.0b deliverable 8): one file in the route's `upload.field`, the other form
 * fields described by the route's `body`. `bind`, the mock handlers and the sweeps share these
 * helpers, so a form is read the same way everywhere. Web-standard `FormData`, `Blob` and `atob`
 * only: no Node built-in and no secure-context API.
 */
/** Standard base64 with padding; empty is allowed. */
export const BASE64_PATTERN = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
/** Bytes of a valid base64 string, without decoding it. */
export function base64ByteLength(base64) {
    const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
    return (base64.length / 4) * 3 - padding;
}
/** The bytes of a base64 string; throws on anything that is not base64. */
export function decodeBase64(base64) {
    if (!BASE64_PATTERN.test(base64))
        throw new TypeError('not a base64 string');
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
}
/** An example file as a `File` (a `Blob` with a name and a type). */
export function exampleFileBlob(file) {
    return new File([decodeBase64(file.base64)], file.name, { type: file.type });
}
/**
 * A form for a multipart request: every defined field of `fields` as a string, then the file in
 * `field`. The caller never sets `Content-Type`: `fetch` writes it with the boundary.
 */
export function buildForm(field, fields, file, fileName) {
    const form = new FormData();
    if (typeof fields === 'object' && fields !== null) {
        for (const [key, value] of Object.entries(fields)) {
            if (value === undefined || value === null)
                continue;
            form.append(key, typeof value === 'string' ? value : String(value));
        }
    }
    const name = fileName ??
        (typeof file.name === 'string' && file.name !== ''
            ? file.name
            : 'upload');
    form.append(field, file, name);
    return form;
}
function isBlob(value) {
    return typeof Blob !== 'undefined' && value instanceof Blob;
}
/**
 * Read a multipart request for a route: 400-style issues for a body that is not a form, a
 * missing file or fields its `body` schema refuses; then 413 `payload_too_large` for a file over
 * `upload.maxBytes` and 415 `unsupported_media_type` for a type outside `upload.mimeTypes`.
 */
export async function readMultipartForm(route, readForm) {
    const upload = route.upload;
    if (upload === undefined)
        throw new TypeError('readMultipartForm needs a multipart route');
    let form;
    try {
        form = await readForm();
    }
    catch {
        return {
            ok: false,
            issues: [
                { path: ['body'], code: 'invalid_form', message: 'The body is not a multipart form.' },
            ],
        };
    }
    const issues = [];
    // No prototype: a field named `constructor`, `toString` or `__proto__` is an own key like any
    // other, so the route's strict body schema sees it and refuses it.
    const fields = Object.create(null);
    for (const [key, value] of form.entries()) {
        if (key === upload.field || Object.hasOwn(fields, key))
            continue;
        fields[key] = value;
    }
    const file = form.get(upload.field);
    if (!isBlob(file)) {
        issues.push({
            path: ['body', upload.field],
            code: 'missing_file',
            message: `The form needs a file in "${upload.field}".`,
        });
    }
    let body = fields;
    if (route.body !== undefined) {
        const result = route.body.safeParse(fields);
        if (result.success) {
            body = result.data;
        }
        else {
            for (const issue of result.error.issues) {
                issues.push({
                    path: ['body', ...issue.path],
                    code: issue.code,
                    message: issue.message,
                });
            }
        }
    }
    if (issues.length > 0 || !isBlob(file))
        return { ok: false, issues };
    if (file.size > upload.maxBytes) {
        return {
            ok: false,
            status: 413,
            code: 'payload_too_large',
            message: `The file is larger than ${upload.maxBytes} bytes.`,
        };
    }
    if (!upload.mimeTypes.includes(file.type)) {
        return {
            ok: false,
            status: 415,
            code: 'unsupported_media_type',
            message: `A file of type "${file.type}" is not accepted here.`,
        };
    }
    return { ok: true, body, file };
}
//# sourceMappingURL=multipart.js.map